import { AnimatePresence } from 'framer-motion'
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import type { Category, Item, Member, Trip } from '../../lib/supabase'
import { buildStats, cadence, type Stats } from '../../lib/stats'
import { clock, deletedFinishes, recapOfTrip } from '../../lib/trips'
import { formatCost } from '../../lib/currency'
import { tintVar } from '../../lib/categoryTint'
import { useItemHistory } from '../../hooks/useItemHistory'
import { useTrips } from '../../hooks/useTrips'
import { useStores } from '../../hooks/useShopping'
import { TripSheets, type TripSheet } from './TripSheets'
import { SheetPanel } from '../SheetPanel'
import { Bars } from './Bars'

// The stats page (opened beside the people in the frame): the household's
// shopping read like a training log — the last 30 days as numbers, trips per
// week, spend per month, staples and how often they come round, the sections
// the money goes to, the stores, and who keeps the list. Numbers in condensed
// Archivo, bars in ink, category colour only as identity with its name beside
// it. Every chart also reads as text (a visually hidden table or the list
// under it). lib/stats.ts computes; this only draws.

export function StatsSheet({ open, top, ...props }: {
  open: boolean
  /** Get started while it has steps left: the phone's one button opens both, the steps first. */
  top?: ReactNode
  householdId: string
  items: Item[]
  categories: Category[]
  members: Member[]
  selfMemberId: string | null
  /** Free: Stats are Plus, so the sheet says what it would show instead. */
  locked?: boolean
  onUpgrade?: () => void
  onClose: () => void
}) {
  // A trip opens over the page as the sheet's sibling: a fixed sheet inside the
  // animated (transformed) panel would be placed against the panel, not the screen.
  const [trip, setTrip] = useState<TripSheet | null>(null)
  return (
    <>
      <AnimatePresence>
        {open && (
          <SheetPanel onClose={props.onClose} ariaLabel={top ? 'Get started and Stats' : 'Stats'} maxHeight="92dvh" layout="scroll" wide>
            {top && <div className="ml-stats__top">{top}</div>}
            {props.locked
              ? <StatsLocked onUpgrade={props.onUpgrade} />
              : <StatsBody {...props} onOpenTrip={setTrip} />}
          </SheetPanel>
        )}
      </AnimatePresence>
      <TripSheets sheet={trip} householdId={props.householdId} items={props.items} categories={props.categories}
        members={props.members} selfMemberId={props.selfMemberId} zIndex={30} onClose={() => setTrip(null)} />
    </>
  )
}

/** Free: what Stats is, and the way to it. Reads nothing. */
function StatsLocked({ onUpgrade }: { onUpgrade?: () => void }) {
  return (
    <div className="ml-stats ml-stats--locked">
      <h2 className="ml-display ml-sheet-title">Stats</h2>
      <p className="ml-caption">
        Stats are part of Plus: your trips per week, time in store, what you spend each month, your staples and
        how often they come round, and who keeps the list.
      </p>
      <button type="button" className="ml-btn ml-btn--primary" onClick={onUpgrade}>Get Plus</button>
    </div>
  )
}

const WEEKDAYS = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays']

function StatsBody({ householdId, items, categories, members, selfMemberId, onOpenTrip, onClose }: {
  onOpenTrip: (trip: TripSheet) => void
  householdId: string
  items: Item[]
  categories: Category[]
  members: Member[]
  selfMemberId: string | null
  onClose: () => void
}) {
  const { rows, loaded } = useItemHistory(householdId, items)
  const { trips, loaded: tripsLoaded } = useTrips(householdId)
  const stores = useStores(householdId)
  const stats = useMemo(() => buildStats(rows, items, trips, new Date()), [rows, items, trips])
  const byId = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories])
  const catName = (id: string | null) => (id && byId.get(id)?.name) || 'Other'
  const tint = (id: string | null) => tintVar(id ? byId.get(id) : null)
  const who = (id: string | null) =>
    id && id === selfMemberId ? 'You' : members.find(m => m.id === id)?.display_name ?? 'Someone who left'
  const money = (amount: number) => formatCost(amount, stats.currency ?? 'EUR')

  return (
    <div className="ml-stats">
      <div className="ml-stats__head">
        <h2 className="ml-display ml-sheet-title">Stats</h2>
        <span className="ml-label">Last 30 days</span>
      </div>

      {!loaded || !tripsLoaded
        ? <p className="ml-caption">Loading…</p>
        : stats.empty
          ? <p className="ml-caption ml-stats__empty">Finish a trip and your stats start here: trips, time in store, staples and what you spend.</p>
          : <>
            <StatsContent stats={stats} catName={catName} tint={tint} who={who} money={money}
              storeName={id => stores.find(s => s.id === id)?.name ?? 'A store since removed'} />
            <RecentTrips trips={trips} storeName={id => (id ? stores.find(s => s.id === id)?.name ?? null : null)}
              onEdit={t => onOpenTrip({ tripId: t.id })}
              onOpen={t => onOpenTrip({ summary: {
                recap: recapOfTrip(t.finished_at!, rows, deletedFinishes(trips)), finishedAt: new Date(t.finished_at!), tripId: t.id,
                storeName: t.store_id ? stores.find(s => s.id === t.store_id)?.name ?? null : null,
                // The cost question is the finisher's; anyone may correct it in Edit trip.
                askCost: !t.finished_by || t.finished_by === selfMemberId,
              } })} />
          </>}

      <button type="button" className="ml-btn ml-btn--secondary ml-btn--wide" onClick={onClose}>Done</button>
    </div>
  )
}

function StatsContent({ stats, catName, tint, who, money, storeName }: {
  stats: Stats
  catName: (id: string | null) => string
  tint: (id: string | null) => string
  who: (id: string | null) => string
  money: (amount: number) => string
  storeName: (id: string) => string
}) {
  const w = stats.window
  const tiles = [
    { value: String(w.trips), label: w.trips === 1 ? 'Trip' : 'Trips' },
    { value: String(w.items), label: 'Items bought' },
    ...(w.avgMs !== null ? [{ value: clock(w.avgMs), label: 'Usual trip' }] : []),
    ...(w.spend ? [{ value: money(w.spend.amount), label: 'Spent' }] : []),
  ]
  const weekLabel = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  const monthLabel = (d: Date) => d.toLocaleDateString(undefined, { month: 'short' })
  const thisMonth = stats.spendByMonth[stats.spendByMonth.length - 1]

  return (
    <>
      <ul className="ml-stats__tiles" aria-label="Last 30 days">
        {tiles.map(t => (
          <li key={t.label}>
            <span className="ml-stats__tile-num ml-num">{t.value}</span>
            <span className="ml-label">{t.label}</span>
          </li>
        ))}
      </ul>

      <section className="ml-stats__section" aria-label="Trips per week">
        <h3 className="ml-label">Trips per week</h3>
        <Bars
          values={stats.weeks.map(x => ({ key: x.start.toISOString(), value: x.trips, label: `Week of ${weekLabel(x.start)}: ${x.trips === 1 ? '1 trip' : `${x.trips} trips`}` }))}
          axis={[weekLabel(stats.weeks[0].start), 'This week']}
          summary={`Trips per week for ${stats.weeks.length} weeks`} />
        {stats.usualDay !== null && <p className="ml-stats__note">You usually shop on {WEEKDAYS[stats.usualDay]}.</p>}
      </section>

      {stats.currency && stats.spendByMonth.some(m => m.amount > 0) && (
        <section className="ml-stats__section" aria-label="Spent per month">
          <h3 className="ml-label">Spent per month</h3>
          <Bars
            values={stats.spendByMonth.map(m => ({ key: m.month.toISOString(), value: m.amount, label: `${monthLabel(m.month)}: ${money(m.amount)}` }))}
            axis={stats.spendByMonth.map(m => monthLabel(m.month))}
            summary={`Spent per month for ${stats.spendByMonth.length} months`} />
          {thisMonth && <p className="ml-stats__note">{money(thisMonth.amount)} so far this month, from the trips that say what they cost.</p>}
        </section>
      )}

      {stats.staples.length > 0 && (
        <section className="ml-stats__section" aria-label="Staples">
          <h3 className="ml-label">Staples</h3>
          <ul className="ml-stats__rows">
            {stats.staples.map(s => (
              <li key={s.name}>
                <span className="ml-stats__name">{s.name}</span>
                <span className="ml-caption">{s.everyDays !== null ? cadence(s.everyDays) : ''}</span>
                <span className="ml-stats__count ml-num">{s.times}×</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {stats.categories.length > 0 && (
        <section className="ml-stats__section" aria-label="Sections">
          <h3 className="ml-label">Sections</h3>
          <div className="ml-stats__stack" aria-hidden="true">
            {stats.categories.map(c => (
              <span key={c.categoryId ?? 'other'} style={{ flexGrow: c.share, '--ml-tint': tint(c.categoryId) } as CSSProperties} />
            ))}
          </div>
          <ul className="ml-stats__rows">
            {stats.categories.map(c => (
              <li key={c.categoryId ?? 'other'} style={{ '--ml-tint': tint(c.categoryId) } as CSSProperties}>
                <span className="ml-stats__name"><span className="ml-dot" aria-hidden="true" />{catName(c.categoryId)}</span>
                <span className="ml-caption ml-num">{c.items === 1 ? '1 item' : `${c.items} items`}</span>
                <span className="ml-stats__count ml-num">{Math.round(c.share * 100)}%</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {stats.stores.length > 0 && (
        <section className="ml-stats__section" aria-label="Stores">
          <h3 className="ml-label">Stores</h3>
          <ul className="ml-stats__rows ml-stats__rows--stores">
            {stats.stores.map(s => (
              <li key={s.storeId}>
                <span className="ml-stats__name">{storeName(s.storeId)}</span>
                <span className="ml-caption ml-num">{s.trips === 1 ? '1 trip' : `${s.trips} trips`}</span>
                <span className="ml-caption ml-num">{s.avgMs !== null ? clock(s.avgMs) : ''}</span>
                <span className="ml-stats__count ml-num">{s.avgSpend !== null ? money(s.avgSpend) : ''}</span>
              </li>
            ))}
          </ul>
          <p className="ml-caption">Usual time and spend per trip, from shopping mode.</p>
        </section>
      )}

      {stats.adders.length > 1 && (
        <section className="ml-stats__section" aria-label="Sharing the load">
          <h3 className="ml-label">Sharing the load</h3>
          <ul className="ml-stats__rows">
            {stats.adders.map(a => (
              <li key={a.memberId ?? 'gone'}>
                <span className="ml-stats__name">{who(a.memberId)}</span>
                <span className="ml-stats__meter" aria-hidden="true">
                  <span style={{ width: `${(a.items / stats.adders[0].items) * 100}%` }} />
                </span>
                <span className="ml-stats__count ml-num">{a.items}</span>
              </li>
            ))}
          </ul>
          <p className="ml-caption">Items put on the list in the last 30 days.</p>
        </section>
      )}
    </>
  )
}

const RECENT = 8

/**
 * Every trip, the latest first (all of them on Show all), each opening its
 * summary. A trip with no cost says so: the header counts them, and its lime
 * Add cost opens Edit trip.
 */
function RecentTrips({ trips, storeName, onOpen, onEdit }: {
  trips: Trip[]
  storeName: (id: string | null) => string | null
  onOpen: (trip: Trip) => void
  onEdit: (trip: Trip) => void
}) {
  const [all, setAll] = useState(false)
  const finished = trips
    .filter(t => t.finished_at && !t.deleted_at && t.finished_at !== t.started_at)
    .sort((a, b) => Date.parse(b.finished_at!) - Date.parse(a.finished_at!))
  if (!finished.length) return null
  const shown = all ? finished : finished.slice(0, RECENT)
  const missing = finished.filter(t => t.cost == null).length
  return (
    <section className="ml-stats__section" aria-label="Trips">
      <div className="ml-stats__trips-head">
        <h3 className="ml-label">Trips</h3>
        {missing > 0 && <span className="ml-label ml-stats__missing">{missing} without a cost</span>}
      </div>
      <ul className="ml-stats__trips">
        {shown.map(t => {
          const when = new Date(t.finished_at!).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
          const where = storeName(t.store_id) ?? 'Somewhere else'
          const priced = t.cost != null && t.currency
          return (
            <li key={t.id} className="ml-stats__trip-row">
              <button type="button" className="ml-stats__trip" onClick={() => onOpen(t)}>
                <span className="ml-stats__name">{where}</span>
                <span className="ml-caption">
                  {when}{t.items != null ? `, ${t.items === 1 ? '1 item' : `${t.items} items`}` : ''}
                  {t.finished_on === 'watch' ? '. On the watch' : ''}
                </span>
                {priced && <span className="ml-stats__count ml-num">{formatCost(t.cost!, t.currency!)}</span>}
              </button>
              {!priced && <button type="button" className="ml-add-cost" onClick={() => onEdit(t)}>Add cost</button>}
            </li>
          )
        })}
      </ul>
      {finished.length > shown.length && (
        <button type="button" className="ml-text-btn ml-stats__more" onClick={() => setAll(true)}>
          Show all {finished.length}
        </button>
      )}
    </section>
  )
}
